import { useEffect, useMemo, useState } from "react";
import { Descriptions, Modal, Table, Typography } from "antd";
import { unstable_batchedUpdates } from "react-dom";
import {
  buildColumnsWithHeaderFilters,
} from "../../Components";
import {
  CommonApi,
  CustomerApi,
  FreightRevenueApi,
  getPermissionRegions,
} from "../../services";
import { DEFAULT_SETTLE_AGREEMENT } from "../../shared/constants/settleAgreement";
import styles from "./Customer.module.scss";

type CustomerDetailModalProps = {
  open: boolean;
  customer: any;
  onClose: () => void;
};

const ROAD_TYPE_OPTIONS = [
  { label: "Bắc - Nam", value: "ROAD" },
  { label: "Cảng", value: "EXIM" },
  { label: "Biên", value: "BORDER" },
  { label: "Đường thủy", value: "WATERWAY" },
  { label: "Chuyển kho", value: "TRANSFER" },
];

const formatAmount = (value: any) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toLocaleString("vi-VN") : "";
};

const formatDistance = (value: any) => {
  const distance = Number(value);
  return Number.isFinite(distance)
    ? `${distance.toLocaleString("vi-VN")} km`
    : "";
};

const buildFreightColumns = (
  regionOptions: Array<{ label: string; value: string }>,
  provinceOptions: Array<{ label: string; value: string }>,
): any[] => [
  {
    title: "Mã tuyến",
    dataIndex: "code",
    width: 160,
    fixed: "left",
    filterDataIndex: "searchText",
    filterConfig: {
      type: "string",
      filterProps: { placeholder: "Nhập mã tuyến" },
    },
  },
  {
    title: "Khu vực",
    dataIndex: ["routeBase", "regionName"],
    width: 140,
    filterDataIndex: "region",
    filterConfig: {
      type: "select",
      filterProps: {
        options: regionOptions,
        showSearch: true,
        optionFilterProp: "label",
        placeholder: "Chọn khu vực",
      },
    },
  },
  {
    title: "Chiều hàng",
    dataIndex: ["routeBase", "direction"],
    width: 130,
  },
  {
    title: "Hình thức VC",
    dataIndex: ["routeBase", "roadType"],
    width: 150,
    filterDataIndex: "roadType",
    filterConfig: {
      type: "select",
      filterProps: {
        options: ROAD_TYPE_OPTIONS,
        placeholder: "Chọn hình thức VC",
      },
    },
    render: (value: string) =>
      ROAD_TYPE_OPTIONS.find((option) => option.value === value)?.label || value,
  },
  {
    title: "Điểm nhận",
    dataIndex: ["routeBase", "receipt", "address"],
    width: 220,
    filterDataIndex: "pReceipt",
    filterConfig: {
      type: "select",
      filterProps: {
        options: provinceOptions,
        showSearch: true,
        optionFilterProp: "label",
        placeholder: "Chọn điểm nhận",
      },
    },
  },
  {
    title: "Điểm trả",
    dataIndex: ["routeBase", "delivery", "address"],
    width: 220,
    filterDataIndex: "pDelivery",
    filterConfig: {
      type: "select",
      filterProps: {
        options: provinceOptions,
        showSearch: true,
        optionFilterProp: "label",
        placeholder: "Chọn điểm trả",
      },
    },
  },
  {
    title: "Km có hàng",
    dataIndex: ["routeBase", "haveGoodsDistance"],
    width: 130,
    render: formatDistance,
  },
  {
    title: "Km không hàng",
    dataIndex: ["routeBase", "noGoodsDistance"],
    width: 140,
    render: formatDistance,
  },
  {
    title: "Cước vận chuyển",
    children: [
      {
        title: "Cont 20",
        dataIndex: ["price", "cont20"],
        width: 130,
        render: formatAmount,
      },
      {
        title: "Cont 40",
        dataIndex: ["price", "cont40"],
        width: 130,
        render: formatAmount,
      },
      {
        title: "Cont 45",
        dataIndex: ["price", "cont45"],
        width: 130,
        render: formatAmount,
      },
    ],
  },
  {
    title: "Cước CY",
    children: [
      {
        title: "20",
        dataIndex: ["price", "cy20"],
        width: 120,
        render: formatAmount,
      },
      {
        title: "40",
        dataIndex: ["price", "cy40"],
        width: 120,
        render: formatAmount,
      },
      {
        title: "45",
        dataIndex: ["price", "cy45"],
        width: 120,
        render: formatAmount,
      },
    ],
  },
  {
    title: "Kẹp lệch",
    dataIndex: ["price", "splitCont20"],
    width: 120,
    render: formatAmount,
  },
  {
    title: "Phí giao nhận",
    children: [
      {
        title: "Xuất",
        dataIndex: ["deliveryCost", "export"],
        width: 120,
        render: formatAmount,
      },
      {
        title: "Nhập",
        dataIndex: ["deliveryCost", "import"],
        width: 120,
        render: formatAmount,
      },
    ],
  },
  {
    title: "Phụ cước",
    children: [
      {
        title: "Xuất",
        dataIndex: ["extraFee", "export"],
        width: 120,
        render: formatAmount,
      },
      {
        title: "Nhập",
        dataIndex: ["extraFee", "import"],
        width: 120,
        render: formatAmount,
      },
    ],
  },
];

export const CustomerDetailModal = ({
  open,
  customer,
  onClose,
}: CustomerDetailModalProps) => {
  const [resolvedCustomer, setResolvedCustomer] = useState<any>(customer);
  const [freightLoading, setFreightLoading] = useState(false);
  const [freightRows, setFreightRows] = useState<any[]>([]);
  const [freightVersion, setFreightVersion] = useState<string | null>(null);
  const [filters, setFilters] = useState<any>({});
  const [regionOptions, setRegionOptions] = useState<any[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    if (!open || !customer?._id) return;

    let active = true;
    unstable_batchedUpdates(() => {
      setResolvedCustomer(customer);
      setFilters({});
      setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
      setFreightRows([]);
      setFreightVersion(null);
      setFreightLoading(true);
    });

    FreightRevenueApi.getFreightVesion({ state: "VALID" })
      .then((versions: any) => {
        if (active) {
          setFreightVersion(versions?.data?.[0]?._id ?? "");
        }
      })
      .catch(() => {
        if (active) setFreightVersion("");
      });

    Promise.allSettled([
      CustomerApi.getCustomer(customer._id),
      getPermissionRegions({}),
      CommonApi.getSearchProvine({ searchText: "" }),
    ])
      .then(([detailResult, regionResult, provinceResult]) => {
        if (!active) return;

        const detail =
          detailResult.status === "fulfilled" ? detailResult.value : customer;
        const regions =
          regionResult.status === "fulfilled" ? regionResult.value : [];
        const provinces =
          provinceResult.status === "fulfilled"
            ? provinceResult.value
            : undefined;

        setResolvedCustomer(detail?._id ? detail : customer);
        setRegionOptions(
          (regions ?? []).map((region: any) => ({
            label: region.name,
            value: region._id,
          })),
        );
        setProvinceOptions(
          (provinces?.data ?? []).map((province: any) => ({
            label: province.name,
            value: province._id,
          })),
        );
      });

    return () => {
      active = false;
    };
  }, [customer, open]);

  useEffect(() => {
    if (!open || !customer?._id || freightVersion === null) return;

    let active = true;
    setFreightLoading(true);
    FreightRevenueApi.getAllFreight({
      current: pagination.current,
      pageSize: pagination.pageSize,
      customer: customer._id,
      version: freightVersion,
      ...filters,
    })
      .then((response: any) => {
        if (!active) return;
        setFreightRows(response?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          total: response?.totalItem ?? 0,
        }));
      })
      .catch(() => {
        if (active) setFreightRows([]);
      })
      .finally(() => {
        if (active) setFreightLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    customer,
    filters,
    freightVersion,
    open,
    pagination.current,
    pagination.pageSize,
  ]);

  const columns = useMemo(
    () =>
      buildColumnsWithHeaderFilters(
        buildFreightColumns(regionOptions, provinceOptions),
        filters,
        (nextFilters: any) => {
          unstable_batchedUpdates(() => {
            setPagination((prev) => ({ ...prev, current: 1 }));
            setFilters((prev: any) =>
              typeof nextFilters === "function"
                ? nextFilters(prev)
                : nextFilters,
            );
          });
        },
      ),
    [filters, provinceOptions, regionOptions],
  );

  const detail = resolvedCustomer ?? customer ?? {};

  return (
    <Modal
      open={open}
      title="Chi tiết khách hàng"
      width="92vw"
      footer={null}
      onCancel={onClose}
      className={styles.DetailModal}
    >
      <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
        <Descriptions.Item label="Mã khách hàng">
          {detail.code || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Tên">{detail.name || "-"}</Descriptions.Item>
        <Descriptions.Item label="Tên đầy đủ">
          {detail.fullName || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Số điện thoại">
          {detail.phoneNumber || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Mã số thuế">
          {detail.businessLicense?.text || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Thời hạn thanh toán">
          {detail.settleAgreement || DEFAULT_SETTLE_AGREEMENT}
        </Descriptions.Item>
        <Descriptions.Item label="Tỉnh/TP">
          {detail.address?.province || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Địa chỉ" span={2}>
          {detail.address?.text || "-"}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Title level={5} className={styles.FreightTitle}>
        Danh sách giá cước ({pagination.total})
      </Typography.Title>
      <div className={styles.FreightTable}>
        <Table
          bordered
          size="small"
          rowKey={(record: any) => record._id}
          loading={freightLoading}
          columns={columns}
          dataSource={freightRows}
          scroll={{ x: "max-content", y: 380 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            onChange: (current, pageSize) =>
              setPagination((prev) => ({
                ...prev,
                current,
                pageSize,
              })),
          }}
        />
      </div>
    </Modal>
  );
};
